import type {
    StateTree,
    DraftDealTemplate,
    DealTemplatesState,
    DealTemplate,
    DealTemplateInventoryTypeExtended,
    ApplyingDealTemplate,
    ScenarioVehicleDetail
} from '@makemydeal/dr-dash-types';
import type { IVehicle } from '@makemydeal/dr-platform-types';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import type { RealState } from '@makemydeal/shared-scoped-store';
import * as offerRedux from './offerRedux';
import * as vehicleSelectors from './vehicleSelectors';

export const getDealTemplate = (state: StateTree): DealTemplatesState | undefined => {
    return state.dealTemplates;
};

export const getDraftDealTemplate = (state: StateTree): DraftDealTemplate | undefined => {
    return getDealTemplate(state)?.draft;
};

export const getAppliedDealTemplateId = (state: StateTree): string | undefined => {
    return getDealTemplate(state)?.applied?.id;
};

export const getIsDealTemplateApplied = (state: StateTree): boolean => {
    return !!getAppliedDealTemplateId(state);
};

export const getAppliedDealTemplateName = (state: StateTree): string | undefined => {
    return state.dealTemplates?.applied?.name;
};

export const getDealTemplateList = (state: StateTree): DealTemplate[] | undefined => {
    return getDealTemplate(state)?.list.value;
};

export const getDealTemplateById = (state: StateTree, id: string): DealTemplate | undefined => {
    return getDealTemplateList(state)?.find((template) => template.id === id);
};

export const getManualDealTemplateList = (state: StateTree): DealTemplate[] | undefined => {
    return getDealTemplate(state)?.manualList.value;
};

export const getIsDealTemplateListLoading = (state: StateTree): boolean | undefined => {
    return getDealTemplate(state)?.list.isLoading;
};

export const getIsManualDealTemplateListLoading = (state: StateTree): boolean | undefined => {
    return getDealTemplate(state)?.manualList.isLoading;
};

export const getManuallyApplyingTemplateState = (state: StateTree): ApplyingDealTemplate | undefined => {
    return getDealTemplate(state)?.manuallyApplying;
};

export const isFirstPencilTemplateApplying = (state: StateTree): boolean => {
    const manuallyApplying = getManuallyApplyingTemplateState(state);
    return manuallyApplying?.isApplying === true && manuallyApplying.source === 'firstPencil';
};

export const isDealTemplateManuallyApplied = (state: StateTree): boolean | undefined => {
    return getDealTemplate(state)?.manuallyApplying?.isApplied;
};

export const getDealTemplateUpdatingStatus = (state: StateTree) => getDealTemplate(state)?.updating;

export const getIsDealTemplateRemoving = (state: StateTree): boolean | undefined => {
    return getDealTemplate(state)?.removing.isRemoving;
};

export const getIsDealTemplateRemoved = (state: StateTree): boolean | undefined => {
    return getDealTemplate(state)?.removing.isRemoved;
};

export const getDealTemplateRemovingInventoryType = (state: StateTree): DealTemplateInventoryTypeExtended | undefined => {
    return getDealTemplate(state)?.removing.inventoryType;
};

export const getIsDealTemplateRemovingError = (state: StateTree): boolean | undefined => {
    return getDealTemplate(state)?.removing.isError;
};

export const getDealTemplateRemovingName = (state: StateTree): string | undefined => {
    return getDealTemplate(state)?.removing.name;
};

export const getSavePaymentTerms = (state: StateTree) => {
    return getDealTemplate(state)?.savePaymentTerms || false;
};

export const getTemplateBuilderMode = (state: StateTree) => {
    return getDealTemplate(state)?.templateBuilderMode;
};

const getVehicleYmmt = (vehicle: IVehicle): string => `${vehicle?.year}|${vehicle?.make}|${vehicle?.model}|${vehicle?.trim}`;

const isSameVehicle = (vehicleA: IVehicle, vehicleB: IVehicle): boolean => {
    if (vehicleA?.vin && vehicleB?.vin) {
        return vehicleA.vin === vehicleB.vin;
    }
    if (vehicleA?.chromeStyleId && vehicleB?.chromeStyleId) {
        return vehicleA.chromeStyleId === vehicleB.chromeStyleId;
    }
    return getVehicleYmmt(vehicleA) === getVehicleYmmt(vehicleB);
};

export const getHasMultipleVehiclesAcrossScenarios = (state: RealState): boolean => {
    const scopesOrder = scopedStateSelectors.getActiveScopesOrder(state);
    if (scopesOrder.length < 2) {
        return false;
    }

    const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state);
    const primaryState = scopedStateSelectors.getScopeStateById(state, primaryScopeId) as StateTree | undefined;
    if (!primaryState) {
        return false;
    }

    const primaryVehicle = vehicleSelectors.getVehicle(primaryState);

    return scopesOrder.some((scopeId) => {
        if (scopeId === primaryScopeId) return false;

        const scopeState = scopedStateSelectors.getScopeStateById(state, scopeId) as StateTree | undefined;
        if (!scopeState) return false;

        const vehicle = vehicleSelectors.getVehicle(scopeState);
        return !isSameVehicle(primaryVehicle, vehicle);
    });
};

export const getScenarioVehicleDetails = (state: RealState): ScenarioVehicleDetail[] => {
    const scopesOrder = scopedStateSelectors.getActiveScopesOrder(state);
    const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state);

    return scopesOrder.map((scopeId) => {
        const isPrimary = scopeId === primaryScopeId;
        const scopeState = scopedStateSelectors.getScopeStateById(state, scopeId) as StateTree | undefined;

        if (!scopeState) {
            return { scopeId, isPrimary };
        }

        const vehicle = vehicleSelectors.getVehicle(scopeState);
        const offerType = offerRedux.getCurrentOfferType(scopeState);

        return {
            scopeId,
            isPrimary,
            year: vehicle.year,
            make: vehicle.make,
            model: vehicle.model,
            trim: vehicle.trim,
            offerType,
            term: offerRedux.getTermLength(scopeState),
            monthlyPayment: offerRedux.getMonthlyPaymentByOfferType(scopeState, offerType),
            rate: offerRedux.getSellRate(scopeState),
            rateType: offerRedux.getRateType(scopeState)
        };
    });
};
